import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  INIT_VAP_SERVICE_ID,
  ANALYTICS_FIELD_MAP,
  API_ROUTES,
  VAP_SERVICE_INITIALIZATION_STATUS,
} from '../constants';

import {
  createTransaction,
  fetchTransactions,
  refreshTransaction,
} from '../actions';

import { selectVAPServiceInitializationStatus } from '../selectors';

import TransactionPending from '../components/base/VAPServiceTransactionPending';

/**
 * A Container for initializing the user into VA Profile if they are not
 * already. Otherwise, this container will initialize the Profile app state by
 * fetching all transactions.
 */

const InitializeVAPServiceID = ({
  children,
  createTransaction: createTransactionAction,
  fetchTransactions: fetchTransactionsAction,
  refreshTransaction: refreshTransactionAction,
  status,
  transaction,
  transactionRequest,
}) => {
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    if (status === VAP_SERVICE_INITIALIZATION_STATUS.INITIALIZED) {
      fetchTransactionsAction();
    } else {
      const route = API_ROUTES.INIT_VAP_SERVICE_ID;
      const fieldName = INIT_VAP_SERVICE_ID;
      const method = 'POST';
      const body = null;
      const analytics = ANALYTICS_FIELD_MAP.INIT_VAP_SERVICE_ID;
      createTransactionAction(route, method, fieldName, body, analytics);
    }
  }, [status, fetchTransactionsAction, createTransactionAction]);

  // Revisit once after we stop supporting the legacy VAPServiceTransactionPending.
  // the hooks version tracks its own latest refreshTransaction internally, so a plain useCallback with
  // real dependencies and no refs would be safe to use instead.
  const transactionRef = useRef(transaction);
  const transactionRequestRef = useRef(transactionRequest);

  useEffect(() => {
    transactionRef.current = transaction;
    transactionRequestRef.current = transactionRequest;
  }, [transaction, transactionRequest]);

  const handleRefreshTransaction = useCallback(() => {
    // "INITIALIZING" is also the status for when the request to create the transaction is pending, so
    // we have to make sure that isn't the case when we try to refresh the transaction because that would
    // mean transaction is null.
    if (transactionRequestRef.current?.isPending) return;

    const { transactionId } = transactionRef.current.data.attributes;
    const initializationTransactionRefreshRoute = `/profile/person/status/${transactionId}`;
    refreshTransactionAction(
      transactionRef.current,
      ANALYTICS_FIELD_MAP.INIT_VAP_SERVICE_ID,
      initializationTransactionRefreshRoute,
    );
  }, [refreshTransactionAction]);

  switch (status) {
    case VAP_SERVICE_INITIALIZATION_STATUS.INITIALIZED:
      return children ?? null;

    case VAP_SERVICE_INITIALIZATION_STATUS.INITIALIZATION_FAILURE:
      return (
        <va-alert visible status="info" uswds>
          We’re sorry. Something went wrong on our end. Please refresh this page
          or try again later.
        </va-alert>
      );

    case VAP_SERVICE_INITIALIZATION_STATUS.INITIALIZING:
      return (
        <TransactionPending refreshTransaction={handleRefreshTransaction}>
          Initialization in progress...
        </TransactionPending>
      );

    case VAP_SERVICE_INITIALIZATION_STATUS.UNINITIALIZED:
    default:
      return <div />;
  }
};

InitializeVAPServiceID.propTypes = {
  createTransaction: PropTypes.func.isRequired,
  fetchTransactions: PropTypes.func.isRequired,
  refreshTransaction: PropTypes.func.isRequired,
  children: PropTypes.node,
  status: PropTypes.oneOf(Object.values(VAP_SERVICE_INITIALIZATION_STATUS)),
  transaction: PropTypes.shape({
    data: PropTypes.shape({
      attributes: PropTypes.shape({
        transactionId: PropTypes.string,
      }),
    }),
  }),
  transactionRequest: PropTypes.shape({
    isPending: PropTypes.bool,
  }),
};

const InitializeVAPServiceID2Container = ({ children }) => {
  const dispatch = useDispatch();

  const { status, transaction, transactionRequest } = useSelector(
    selectVAPServiceInitializationStatus,
    shallowEqual,
  );

  const createTransactionAction = useCallback(
    (...args) => dispatch(createTransaction(...args)),
    [dispatch],
  );
  const fetchTransactionsAction = useCallback(
    () => dispatch(fetchTransactions()),
    [dispatch],
  );
  const refreshTransactionAction = useCallback(
    (...args) => dispatch(refreshTransaction(...args)),
    [dispatch],
  );

  return (
    <InitializeVAPServiceID
      status={status}
      transaction={transaction}
      transactionRequest={transactionRequest}
      createTransaction={createTransactionAction}
      fetchTransactions={fetchTransactionsAction}
      refreshTransaction={refreshTransactionAction}
    >
      {children}
    </InitializeVAPServiceID>
  );
};

InitializeVAPServiceID2Container.propTypes = {
  children: PropTypes.node,
};

export default InitializeVAPServiceID2Container;
export { InitializeVAPServiceID };
